// src/ui/steps/step4-result.jsx: Openingsbod (o.b.v. timesheet) + aannames + export
import React from "react";
import { FW, FwButton, FwCard, StepHeader, ResultaatSamenvatting, DeelResultaat } from "../primitives";
import { calc } from "../../lib/calc";
import { config } from "../../lib/config";
import { niveauIndicatorenFilmAv, sectieLabels } from "../../data/niveau-indicatoren";
import { bulletsVoorRol, omschrijvingVoorRol } from "../../data/rollen";
import { exportFilmAv, disclaimerTekst } from "../../lib/docx-export";

// Stap-indices binnen de film/AV-flow (App.jsx: scope=0, rol=1, timesheet=2,
// niveau=3, result=4). Worden gebruikt door de 'Aanpassen'-knoppen in de
// onderbouwing om direct naar de juiste stap te springen.
const STAP_ROL_OMSCHRIJVING = 1;
const STAP_TIMESHEET = 2;
const STAP_NIVEAU = 3;

export const StepResult = ({ state, setState, back, goToStep, restart }) => {
  const opdracht = calc.berekenOpdracht(state);
  const huidigeRol = state.rollen?.[0];
  const rolResultaat = opdracht.rolResultaten?.[0]?.result;

  if (!rolResultaat || !huidigeRol) {
    return <div style={{ padding: 40, color: FW.navy }}>Geen berekening mogelijk: ga terug en vul de timesheet in.</div>;
  }

  const isFestival = state.type === "festival";

  const emailText = React.useMemo(
    () => (huidigeRol && rolResultaat ? generateEmail(state, opdracht, huidigeRol, rolResultaat) : ""),
    [state, opdracht, huidigeRol, rolResultaat],
  );
  const downloadWord = () => exportFilmAv({ state, opdracht, huidigeRol, rolResultaat });

  return (
    <div className="result-page">
      <StepHeader
        tag="Film/AV-productie"
        perspectief={state.perspectief}
        title="Dit is jouw ondergrens voor onderhandeling"
        sub={<>Op basis van de keuzes die je hebt gemaakt, zie je hieronder een onderbouwd starttarief en een inschatting van de totale vergoeding. Het starttarief vormt de <b>ondergrens</b> die past bij het niveau van de opdracht. Ervaring, reputatie of specialisme en de complexiteit van het werk en de productie kunnen de hoogte van je tarief beïnvloeden. Houd hier rekening mee bij het bepalen van je openingsbod tijdens de onderhandeling.</>}
        onBack={back}
      />

      <ResultaatSamenvatting
        isFestival={isFestival}
        vorm={state.vorm}
        rol={huidigeRol.rol}
        dep={huidigeRol.dep}
        niveau={huidigeRol.niveau}
      />

      <div className="row-2col" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24, alignItems: "start" }}>
        <div>
          <div style={{ background: FW.white, border: `1.5px solid ${FW.gray100}`, borderRadius: 20, padding: 24 }}>
            <UrenCategorieRegel label="Voorbereiding" uren={rolResultaat.urenVoorbereiding} uurtarief={rolResultaat.uurtarief} details={huidigeRol.urenDetails?.voorbereiding} />
            <UrenCategorieRegel label="Hoofdwerkzaamheden" uren={rolResultaat.urenHoofdwerk} uurtarief={rolResultaat.uurtarief} details={huidigeRol.urenDetails?.hoofdwerk} />
            <UrenCategorieRegel label="Afronding" uren={rolResultaat.urenAfronding} uurtarief={rolResultaat.uurtarief} details={huidigeRol.urenDetails?.afronding} />
            {rolResultaat.urenPrPromotie > 0 && (
              <UrenCategorieRegel label="PR & promotie" uren={rolResultaat.urenPrPromotie} uurtarief={rolResultaat.uurtarief} details={huidigeRol.urenDetails?.pr_promotie} />
            )}
            <div style={{ height: 1, background: FW.gray100, margin: "12px 0" }} />
            <Regel label={`Subtotaal Werkzaamheden · ${rolResultaat.urenTotaal} uur`} bedrag={opdracht.subtotaalArbeid} strong />

            <div style={{ height: 1, background: FW.gray100, margin: "12px 0" }} />
            <Regel label="Totaal excl. btw" bedrag={opdracht.totaalExBtw} strong />
            <Regel label="21% btw" bedrag={opdracht.btw} />
            <Regel label="Totaal incl. btw" bedrag={opdracht.totaalInclBtw} strong />
          </div>

          <div style={{ marginTop: 32, fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Onderbouwing van jouw openingsbod</div>
          <div style={{ fontSize: 14, color: FW.navy2, marginBottom: 16 }}>
            Klik op de aparte elementen om te kijken welke onderbouwing je hebt ingevuld of om deze aan te passen.
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {state.omschrijving && (
              <Aanname
                label="Omschrijving"
                waarde={state.omschrijving.length > 80 ? state.omschrijving.slice(0, 80).trim() + "…" : state.omschrijving}
                uitleg="Dit is de tekst die je hebt ingevuld als omschrijving van de opdracht. De digitale assistent gebruikt deze tekst om een rol en zwaarteniveau te suggereren."
                onEdit={() => goToStep(STAP_ROL_OMSCHRIJVING)}
                detail={
                  <div style={{ background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: 12, fontSize: 14, color: FW.navy2, lineHeight: 1.55, whiteSpace: "pre-wrap", marginBottom: 12 }}>
                    {state.omschrijving}
                  </div>
                }
              />
            )}
            <Aanname
              label="Rol / functie"
              waarde={`${huidigeRol.rol} · ${huidigeRol.dep}`}
              uitleg={`Op basis van ${state.omschrijving ? "jouw omschrijving" : "jouw keuze in de rollen-lijst"} is deze rol gekozen uit de Gids Rollen Film/AV${isFestival ? " en Functiehandboek Filmfestivals" : ""}.`}
              onEdit={() => goToStep(STAP_ROL_OMSCHRIJVING)}
              detail={<RolOmschrijvingDetail rol={huidigeRol.rol} dep={huidigeRol.dep} isFestival={isFestival} />}
            />
            <Aanname
              label="Zwaarteniveau"
              waarde={`${huidigeRol.niveau}${rolResultaat.tariefRij?.domein ? ` (${rolResultaat.tariefRij.domein})` : (rolResultaat.tariefRij?.rol_voorbeeld ? ` (${rolResultaat.tariefRij.rol_voorbeeld})` : "")}`}
              uitleg={`Het Opdracht Kompas koppelt elke opdracht aan een zwaarteniveau. Niveau ${huidigeRol.niveau} geeft het minimum starttarief van ${calc.fmt(rolResultaat.uurtarief)}/u.`}
              onEdit={() => goToStep(STAP_NIVEAU)}
              detail={!isFestival ? <ZwaarteNiveauDetail niveau={huidigeRol.niveau} /> : undefined}
            />
            <Aanname
              label="Opslagfactor"
              waarde={isFestival ? "69,26% (digiPACCT)" : "74,45% (Ketentafel Film/AV)"}
              uitleg={isFestival
                ? "Voor filmfestivals geldt de digiPACCT-methode: bedrijfskosten (15%), niet-declarabele uren (17%), vakantietoeslag (8%), ziekte/arbeidsongeschiktheid (14,5%), ZVW (5,32%) en pensioen (9,44%). Samen: 69,26%."
                : "De opslagfactor dekt niet-declarabele kosten (15%), niet-declarabele tijd (25%), vakantietoeslag (8%), eindejaarsuitkering (3,45%), ZVW (6%), arbeidsongeschiktheid (7%) en pensioen (10%). Samen: 74,45%."}
              detail={<OpslagTabel isFestival={isFestival} />}
            />
            <Aanname
              label="Voorbereiding"
              waarde={`${rolResultaat.urenVoorbereiding} uur`}
              onEdit={() => goToStep(STAP_TIMESHEET)}
              detail={<UrenDetails details={huidigeRol.urenDetails?.voorbereiding} />}
            />
            <Aanname
              label="Hoofdwerkzaamheden"
              waarde={`${rolResultaat.urenHoofdwerk} uur`}
              onEdit={() => goToStep(STAP_TIMESHEET)}
              detail={<UrenDetails details={huidigeRol.urenDetails?.hoofdwerk} />}
            />
            <Aanname
              label="Afronding"
              waarde={`${rolResultaat.urenAfronding} uur`}
              onEdit={() => goToStep(STAP_TIMESHEET)}
              detail={<UrenDetails details={huidigeRol.urenDetails?.afronding} />}
            />
            {rolResultaat.urenPrPromotie > 0 && (
              <Aanname
                label="PR & promotie"
                waarde={`${rolResultaat.urenPrPromotie} uur`}
                onEdit={() => goToStep(STAP_TIMESHEET)}
                detail={<UrenDetails details={huidigeRol.urenDetails?.pr_promotie} />}
              />
            )}
          </div>
        </div>

        <div className="aside-card" style={{ display: "grid", gap: 16, position: "sticky", top: 20 }}>
          <FwCard padded={false} style={{ padding: 22, background: FW.cream }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <i className="ri-lightbulb-line" /> Goed om te weten
            </div>
            <ul style={{ fontSize: 14, color: FW.navy2, margin: 0, paddingLeft: 16, lineHeight: 1.55, display: "grid", gap: 8 }}>
              <li>Dit is een minimum starttarief volgens de Fair Pay-richtlijn.</li>
              <li>De complexiteit van de opdracht, jouw ervaring, reputatie of specialisme en de specifieke aard van de productie kun je meenemen in je onderhandeling.</li>
              <li>Over eventuele reiskosten, apparatuur, materialen en inhuur derden kun je separaat afspraken maken en apart factureren.</li>
              <li>Het zzp-starttarief is inclusief een opslagfactor van {isFestival ? "69,26%" : "74,45%"} voor onder meer niet-declarabele tijd, niet-declarabele kosten, vakantie, pensioen en zorgverzekeringspremies.</li>
            </ul>
          </FwCard>
        </div>
      </div>

      <FwCard tint="navy" padded={false} className="summary-card" style={{ padding: "40px 44px", marginTop: 32 }}>
        <div className="summary-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 32, alignItems: "end" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.yellow, opacity: .9 }}>Start uurtarief</div>
            <div className="summary-value" style={{ fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
              {calc.fmt(rolResultaat.uurtarief)}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 6 }}>Incl. {isFestival ? "69,26%" : "74,45%"} opslag</div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.yellow, opacity: .9 }}>Tijdsinschatting</div>
            <div className="summary-value" style={{ fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
              {rolResultaat.urenTotaal}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 6 }}>uur in totaal</div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.yellow, opacity: .9 }}>Openingsbod</div>
            <div className="summary-value" style={{ fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
              {calc.fmtInt(rolResultaat.openingsbod)}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 6 }}>
              Excl. btw · incl: {calc.fmtInt(opdracht.totaalInclBtw)}
            </div>
          </div>
        </div>
      </FwCard>

      <DeelResultaat state={state} emailText={emailText} onDownload={downloadWord} titel="Download hier de bovenstaande gegevens" />

      <div style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }} className="hide-print step-nav">
        <FwButton variant="ghost" arrow={false} onClick={back}>← Terug</FwButton>
        <FwButton variant="secondary" arrow={false} onClick={restart}>Nieuwe berekening</FwButton>
      </div>
    </div>
  );
};

const UrenCategorieRegel = ({ label, uren, uurtarief, details }) => {
  if (!uren || uren <= 0) return null;
  const details_ingevuld = Array.isArray(details) ? details.filter(d => d.activiteit && (Number(d.uren) || 0) > 0) : [];
  return (
    <div style={{ padding: "4px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "8px 0", fontSize: 15, color: FW.navy }}>
        <span>{label} · {uren} uur × {calc.fmt(uurtarief)}</span>
        <span style={{ fontWeight: 700 }}>{calc.fmt(uren * uurtarief)}</span>
      </div>
      {details_ingevuld.length > 0 && (
        <div style={{ paddingLeft: 14, fontSize: 13, color: FW.gray500, display: "grid", gap: 2, marginTop: -4, marginBottom: 4 }}>
          {details_ingevuld.map(d => (
            <div key={d.id}>{d.activiteit} ({d.uren} uur)</div>
          ))}
        </div>
      )}
    </div>
  );
};

const UrenDetails = ({ details }) => {
  const rijen = Array.isArray(details) ? details.filter(d => d.activiteit && (Number(d.uren) || 0) > 0) : [];
  if (!rijen.length) return null;
  return (
    <div style={{ background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: 12, fontSize: 13, marginBottom: 12 }}>
      {rijen.map(d => (
        <div key={d.id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
          <span style={{ color: FW.navy2 }}>{d.activiteit}</span>
          <span style={{ fontWeight: 700, color: FW.navy }}>{d.uren} uur</span>
        </div>
      ))}
    </div>
  );
};

export const Regel = ({ label, bedrag, strong }) => (
  <div style={{
    display: "flex", justifyContent: "space-between", alignItems: "baseline",
    padding: "8px 0", fontSize: strong ? 17 : 15, fontWeight: strong ? 700 : 400, color: FW.navy,
  }}>
    <span>{label}</span>
    <span style={{ fontWeight: 700 }}>{calc.fmt(bedrag)}</span>
  </div>
);

export const Aanname = ({ label, waarde, uitleg, detail, onEdit }) => {
  const [open, setOpen] = React.useState(false);

  return (
    <div style={{ background: FW.white, border: `1.5px solid ${FW.gray100}`, borderRadius: 14, overflow: "hidden" }}>
      <div onClick={() => setOpen(!open)} style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 10,
        padding: "14px 18px", cursor: "pointer",
      }}>
        <div className="aanname-row" style={{ display: "flex", alignItems: "baseline", gap: 12, flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: FW.gray500, textTransform: "uppercase", letterSpacing: 0.5, minWidth: 160 }}>{label}</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: FW.navy, wordBreak: "break-word" }}>{waarde}</div>
        </div>
        <i className="ri-arrow-down-s-line" style={{
          fontSize: 22, color: FW.gray500,
          transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform .15s",
        }} />
      </div>
      {open && (
        <div style={{ padding: "0 18px 18px", background: FW.cream }}>
          {uitleg && (
            <div style={{ fontSize: 14, lineHeight: 1.5, color: FW.navy2, padding: "6px 0 12px" }}>{uitleg}</div>
          )}
          {detail}
          {onEdit && (
            <button onClick={onEdit} style={{
              background: "transparent", color: FW.navy, border: `1.5px solid ${FW.navy}`,
              borderRadius: 999, padding: "6px 14px", fontWeight: 700, fontSize: 13,
              cursor: "pointer", fontFamily: "var(--font-sans)",
            }}>
              <i className="ri-pencil-line" style={{ marginRight: 6 }} /> Aanpassen
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Detail-render voor de Rol-aanname: bullets per rol uit de Gids Rollen
// (Film/AV, doc 6 kolom D). Festival valt terug op de omschrijving uit het
// Functiehandboek Filmfestivals (bron A, statisch in rollen-omschrijvingen).
export const RolOmschrijvingDetail = ({ rol, dep, isFestival }) => {
  if (!isFestival) {
    const bullets = bulletsVoorRol(dep, rol);
    if (!bullets?.length) return null;
    return (
      <div style={{ background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: 12, marginBottom: 12 }}>
        <ul style={{ fontSize: 14, lineHeight: 1.55, color: FW.navy2, margin: 0, paddingLeft: 20 }}>
          {bullets.map((b, i) => (
            <li key={i} style={{ marginBottom: 4 }}>{b}</li>
          ))}
        </ul>
      </div>
    );
  }
  const omschrijving = omschrijvingVoorRol("festival", dep, rol);
  if (!omschrijving) return null;
  return (
    <div style={{ background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: 12, fontSize: 14, color: FW.navy2, lineHeight: 1.55, marginBottom: 12, whiteSpace: "pre-line" }}>
      {omschrijving}
    </div>
  );
};

// Detail-render voor de Zwaarteniveau-aanname (alleen Film & AV): USB-punten
// en sectie-indicatoren per niveau. De rol-specifieke bullets staan al onder
// de Rol-aanname (RolOmschrijvingDetail): single source of truth via de Gids.
export const ZwaarteNiveauDetail = ({ niveau }) => {
  const indicatoren = niveauIndicatorenFilmAv[niveau];
  if (!indicatoren) return null;
  return (
    <div style={{ marginBottom: 12, background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8, gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: FW.navy, textTransform: "uppercase", letterSpacing: 0.5 }}>
          Niveau-indicatoren: niveau {niveau}
        </div>
        <div style={{ fontSize: 12, color: FW.gray500 }}>USB-punten {indicatoren.usb}</div>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.55, color: FW.navy2 }}>
        {Object.entries(indicatoren.secties).map(([key, tekst]) => (
          <div key={key} style={{ marginBottom: 8 }}>
            <div style={{ fontWeight: 700, color: FW.navy, marginBottom: 2 }}>{sectieLabels[key]}</div>
            <div>{tekst}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const OpslagTabel = ({ isFestival }) => {
  const componenten = isFestival ? config.opslag_festival : config.opslag_film_av;
  const totaalLabel = isFestival ? "69,26%" : "74,45%";
  return (
    <div style={{ background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: 12, fontSize: 13, marginBottom: 12 }}>
      {componenten.map((c, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: i < componenten.length - 1 ? `1px dashed ${FW.gray100}` : "none" }}>
          <span>{c.label}</span><span style={{ fontWeight: 700 }}>{(c.pct*100).toFixed(2).replace(".00","").replace(".",",")}%</span>
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0 2px", marginTop: 4, borderTop: `1.5px solid ${FW.navy}`, fontWeight: 700 }}>
        <span>Totaal</span><span>{totaalLabel}</span>
      </div>
    </div>
  );
};

// Kopieer-tekst Film & AV: spiegelt de Word-export (exportFilmAv), met als
// enige uitzondering het blok "Aandachtspunten voor de onderhandeling".
function generateEmail(state, opdracht, rol, rolResultaat) {
  const cats = [
    { key: "voorbereiding", label: "Voorbereiding", uren: rolResultaat.urenVoorbereiding },
    { key: "hoofdwerk", label: "Hoofdwerkzaamheden", uren: rolResultaat.urenHoofdwerk },
    { key: "afronding", label: "Afronding", uren: rolResultaat.urenAfronding },
    { key: "pr_promotie", label: "PR & promotie", uren: rolResultaat.urenPrPromotie },
  ].filter(c => c.uren > 0);

  const urenDetails = rol.urenDetails || {};
  const detailRegels = (cat) => {
    const rijen = Array.isArray(urenDetails[cat]) ? urenDetails[cat].filter(d => d.activiteit && (Number(d.uren) || 0) > 0) : [];
    return rijen.map(r => `  - ${r.activiteit}: ${r.uren} uur`);
  };

  const L = [];
  L.push("Beste [opdrachtgever],", "");
  L.push("Openingsbod film/AV-productie");
  L.push(`Voor mijn inzet als ${rol.rol} (${rol.dep}).`, "");

  L.push("Samenvatting");
  L.push("- Type opdracht: Film/AV-productie (zzp)");
  L.push(`- Rol · departement: ${rol.rol} · ${rol.dep}`);
  L.push(`- Zwaarteniveau: ${rol.niveau}${rolResultaat.tariefRij?.domein ? ` (${rolResultaat.tariefRij.domein})` : ""}`);
  L.push(`- Uurtarief: ${calc.fmt(rolResultaat.uurtarief)} (incl. 74,45% opslag)`);
  L.push(`- Voorbeeld dagtarief (8,5 u): ${calc.fmt(rolResultaat.uurtarief * 8.5)}`);
  L.push(`- Voorbeeld dagtarief (9,5 u): ${calc.fmt(rolResultaat.uurtarief * 9.5)}`);
  L.push(`- Totaal uren: ${rolResultaat.urenTotaal} uur`);
  L.push(`- Openingsbod (excl. btw): ${calc.fmtInt(rolResultaat.openingsbod)}`);
  L.push(`- Totaal incl. 21% btw: ${calc.fmtInt(opdracht.totaalInclBtw)}`, "");

  L.push(`Rol: ${rol.rol} · ${rol.dep}`);
  L.push("Op basis van jouw keuze in de rollen-lijst is deze rol gekozen uit de Gids Rollen Film/AV.");
  const bullets = bulletsVoorRol(rol.dep, rol.rol);
  if (bullets?.length) L.push(...bullets.map(b => `- ${b}`));
  L.push("");

  L.push(`Zwaarteniveau ${rol.niveau}`);
  L.push(`Het Opdracht Kompas koppelt elke opdracht aan een zwaarteniveau. Niveau ${rol.niveau} geeft het minimum starttarief van ${calc.fmt(rolResultaat.uurtarief)}/u.`, "");
  const ind = niveauIndicatorenFilmAv[rol.niveau];
  if (ind) {
    L.push(`Niveau-indicatoren: niveau ${rol.niveau} · USB-punten ${ind.usb}`);
    Object.entries(ind.secties).forEach(([k, t]) => L.push(`${sectieLabels[k] || k}: ${t}`));
    L.push("");
  }

  if (state.omschrijving) L.push("Omschrijving van de opdracht", state.omschrijving, "");

  L.push("Tijdsinvestering per categorie");
  cats.forEach(c => {
    L.push(`${c.label}: ${c.uren} uur`);
    L.push(...detailRegels(c.key));
  });
  L.push("");

  L.push("Openingsaanbod");
  cats.forEach(c => L.push(`${c.label}: ${c.uren} uur × ${calc.fmt(rolResultaat.uurtarief)} = ${calc.fmt(c.uren * rolResultaat.uurtarief)}`));
  L.push(`Subtotaal opdracht (${rolResultaat.urenTotaal} uur): ${calc.fmt(opdracht.subtotaalArbeid)}`);
  L.push(`21% btw: ${calc.fmt(opdracht.btw)}`);
  L.push(`Totaal incl. btw: ${calc.fmt(opdracht.totaalInclBtw)}`, "");

  L.push("Goed om te benoemen");
  L.push("- Dit is een minimum starttarief volgens Fair Pay richtlijnen 2026. Ervaring of specifieke expertise zijn onderhandelingsruimte.");
  L.push("- Voor reiskosten, apparatuur, materialen of inhuur derden kun je separaat afspraken maken.");
  L.push("- Het tarief bevat al een opslag van 74,45% voor o.a. zzp-lasten, niet-declarabele-tijd en niet-declarabele kosten (vakantie, pensioen, ZVW).", "");

  L.push(disclaimerTekst("film/AV-sector"), "");
  L.push("Met vriendelijke groet,", "[jouw naam]");

  return L.join("\n");
}
